/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  BaseLevelSnippetResp,
  CSVSnippetObj,
  CampaignIdToMetadataMap,
  Link,
  SnipPathToDataMap,
  SnippetBreakdownResp,
  StoredCampaignBreakdowns,
  StoredSnippetBreakdowns,
  Variation,
} from '../types';
import { SnippetPathTree, getSnippetPathTree } from './getSnippetPathTree';

const getVariationRow = (variation: Variation) => {
  const { url, metrics } = variation;
  return { url, ...metrics };
};

const getLinkRow = (link: Link) => {
  const { templateUrl, metrics, variations } = link;
  const variationRows = variations.map((v) => getVariationRow(v));
  return [{ dynamicUrl: templateUrl, ...metrics }, ...variationRows];
};

const getSnippetRow = (snip: SnippetBreakdownResp, snipPathToDataMap: SnipPathToDataMap) => {
  const { name, parentSnippetPath, metrics, links } = snip;
  const snippetRow = {
    snippetName: name,
    parentSnippet: parentSnippetPath ? snipPathToDataMap[parentSnippetPath]?.name : '',
    ...metrics,
    campaignName: '',
  };
  const linkRows = links.flatMap((l) => getLinkRow(l));
  return [snippetRow, ...linkRows] as CSVSnippetObj[];
};

const getSnippetRowsRecursive = (
  snip: SnippetBreakdownResp,
  children: SnippetPathTree[],
  snipPathToDataMap: SnipPathToDataMap,
): CSVSnippetObj[] => {
  const currSnippetRow = getSnippetRow(snip, snipPathToDataMap);
  const childrenRows = children?.length
    ? children.reduce((agg, child) => {
        const childSnipRows = getSnippetRowsRecursive(
          snipPathToDataMap[child.path],
          child.children,
          snipPathToDataMap,
        );
        return [...agg, ...childSnipRows];
      }, [] as CSVSnippetObj[])
    : [];
  return [...currSnippetRow, ...childrenRows];
};

const getSnippetBreakdownRows = (
  baseLevelSnippetId: number,
  storedSnippetBreakdowns: StoredSnippetBreakdowns,
): CSVSnippetObj[] | undefined => {
  const breakdown = storedSnippetBreakdowns[baseLevelSnippetId];
  if (!breakdown) return undefined;

  const paths = breakdown.map((snip) => snip.snippetPath);
  const tree = getSnippetPathTree(paths, baseLevelSnippetId.toString());

  const snipPathToDataMap = breakdown.reduce(
    (map, snip) => ({ ...map, [snip.snippetPath]: snip }),
    {} as SnipPathToDataMap,
  );

  return getSnippetRowsRecursive(
    snipPathToDataMap[baseLevelSnippetId.toString()],
    tree.children,
    snipPathToDataMap,
  );
};

const getCampaignBreakdownRows = (
  snippetData: BaseLevelSnippetResp,
  storedCampaignBreakdowns: StoredCampaignBreakdowns,
  campaignIdToMetadataMap: CampaignIdToMetadataMap,
): CSVSnippetObj[] | undefined => {
  const breakdown = storedCampaignBreakdowns[snippetData.id.toString()];
  if (!breakdown) return undefined;

  const { name, metrics } = snippetData;
  return breakdown.reduce(
    (agg, camp) => {
      const { campaignName = 'Undefined' } = campaignIdToMetadataMap[camp.id.toString()];
      return [...agg, { campaignName, ...camp.metrics }];
    },
    [{ snippetName: name, ...metrics }] as CSVSnippetObj[],
  );
};

export const getCsvRows = (
  baseLevelSnippetData: BaseLevelSnippetResp[] = [],
  storedSnippetBreakdowns: StoredSnippetBreakdowns,
  storedCampaignBreakdowns: StoredCampaignBreakdowns,
  campaignIdToMetadataMap: CampaignIdToMetadataMap,
  expandedBaseLevelSnippetIds: number[],
  showCampaigns: boolean,
) =>
  baseLevelSnippetData.reduce((agg, snip) => {
    const { id, name, metrics } = snip;
    const baseSnipRow = { snippetName: name, ...metrics };

    if (expandedBaseLevelSnippetIds.includes(id)) {
      const expandedRows = showCampaigns
        ? getCampaignBreakdownRows(snip, storedCampaignBreakdowns, campaignIdToMetadataMap)
        : getSnippetBreakdownRows(id, storedSnippetBreakdowns);
      return [...agg, ...(expandedRows || [baseSnipRow])] as CSVSnippetObj[];
    } else {
      return [...agg, baseSnipRow];
    }
  }, [] as CSVSnippetObj[]);
